// AdminOperationsPage.tsx — Admin → Operations (L5): what the system is doing, what's wrong, and the fix, without
// scripts or SQL. Six tabs, each with a count of what NEEDS ATTENTION (not a total); every number comes from one
// GET /ops/overview computed in the backend. Refreshes every 30 s. Actions go through the system's own routes and
// open the progress window.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { opsOverviewApi, type ReadinessWarningRow, type OpsOverview, type OpsRunRow, type OpsThreshold, type Severity, type HealthCheck, type ProviderState, type TestAlertAnswer } from "../../api/operations.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { testAlertLines, TEST_ALERT_WORDS as TW } from "@/lib/test-alert.ts";   // (B122)
import { useProgress } from "@/components/ui/ProgressContext.tsx";
import { modeLabel, engineLine, savedLine } from "@/lib/ops-settings.ts";   // (B113) when a change applies; the engine's own value

type Tab = "alerts" | "runs" | "schedules" | "providers" | "spend" | "health";
const TABS: { id: Tab; label: string }[] = [
  { id: "alerts", label: "Alerts" }, { id: "runs", label: "Runs" }, { id: "schedules", label: "Schedules" },
  { id: "providers", label: "Providers" }, { id: "spend", label: "Spend" }, { id: "health", label: "Health" },
];
const SEV: Record<Severity, { bg: string; fg: string }> = { red: { bg: "#fee2e2", fg: "#b91c1c" }, amber: { bg: "#fef3c7", fg: "#92400e" }, grey: { bg: "#f3f4f6", fg: "#6b7280" } };
const usd = (c: number) => `$${(c / 100).toFixed(2)}`;
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 12 };
const th = { textAlign: "left" as const, fontSize: 12, color: "#6b7280", fontWeight: 600, padding: "6px 8px", borderBottom: "1px solid #e5e7eb" };
const td = { fontSize: 13, padding: "6px 8px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" as const };
const btn = (primary = false) => ({ fontSize: 12, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
  border: primary ? "none" : "1px solid #d1d5db", background: primary ? "#111827" : "#fff", color: primary ? "#fff" : "#111827" });
/** A button that LOOKS disabled when it is (a disabled button styled like an enabled one invites a pointless click). */
const actionBtn = (primary: boolean, disabled: boolean) => ({ ...btn(primary), opacity: disabled ? 0.35 : 1, cursor: disabled ? "not-allowed" : "pointer" });

export function AdminOperationsPage() {
  const [o, setO] = useState<OpsOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab | null>(null);
  const load = useCallback(() => { opsOverviewApi.overview().then((r) => { setO(r); setErr(null); }).catch((e) => setErr(String(e))); }, []);  // catch-reason: the error is shown to the person (setErr)
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);
  // open the first tab with a red badge, else Alerts
  useEffect(() => { if (o && tab === null) setTab((TABS.find((t) => (o.tabs[t.id] as { severity: Severity }).severity === "red")?.id) ?? "alerts"); }, [o, tab]);

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Operations</h1>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{o ? `updated ${new Date(o.generatedAt).toLocaleTimeString()} · refreshes every 30 s` : "loading…"}
          <button style={{ ...btn(), marginLeft: 8 }} aria-label="Refresh" onClick={load}>⟳</button></span>
      </div>
      <p style={{ color: "#6b7280", fontSize: 13, margin: "4px 0 16px" }}>What the system is doing, what needs you, and the fix. Each count is what needs attention — a calm system reads zero.</p>
      {err && <div style={{ ...card, borderColor: "#fca5a5", background: "#fef2f2", color: "#b91c1c" }}>{err}</div>}
      {o && (
        <>
          <div role="tablist" style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", marginBottom: 14 }}>
            {TABS.map((t) => {
              const s = o.tabs[t.id] as { severity: Severity; count?: number | null; thisHourUsdCents?: number };
              const badge = t.id === "spend" ? `${usd(s.thisHourUsdCents ?? 0)}/h` : s.count == null ? "–" : String(s.count);
              const hover = t.id === "runs" && o.tabs.runs.breakdown ? Object.entries(o.tabs.runs.breakdown).map(([k, v]) => `${v} ${k}`).join(" · ") : undefined;
              return (
                <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} title={hover}
                  style={{ padding: "8px 12px", border: "none", background: "none", cursor: "pointer", fontSize: 14, fontWeight: 600,
                    borderBottom: tab === t.id ? "2px solid #111827" : "2px solid transparent", color: tab === t.id ? "#111827" : "#6b7280" }}>
                  {t.label} <span style={{ marginLeft: 4, padding: "1px 7px", borderRadius: 10, fontSize: 12, background: SEV[s.severity].bg, color: SEV[s.severity].fg }}>{badge}</span>
                </button>);
            })}
          </div>
          {tab === "alerts" && <AlertsTab o={o} go={setTab} onChanged={load} />}
          {tab === "runs" && <><ReadinessWarnings /><RunsTab o={o} onChanged={load} /></>}
          {tab === "schedules" && <SchedulesTab o={o} onChanged={load} />}
          {tab === "providers" && <ProvidersTab o={o} onChanged={load} />}
          {tab === "spend" && <SpendTab o={o} />}
          {tab === "health" && <HealthTab o={o} onChanged={load} />}
        </>
      )}
    </div>
  );
}

function AlertsTab({ o, go, onChanged }: { o: OpsOverview; go: (t: Tab) => void; onChanged: () => void }) {
  return (<>
    {!o.alerts.length ? <div style={{ ...card, textAlign: "center", color: "#6b7280" }}>Nothing needs you.</div> : (
    <div style={card}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th}></th><th style={th}>What</th><th style={th}>Threshold</th><th style={th}></th></tr></thead>
        <tbody>{o.alerts.map((a) => (
          <tr key={a.id}>
            <td style={td}>{a.severity === "red" ? "🔴" : "🟠"}</td>
            <td style={td}>{a.what}</td>
            <td style={{ ...td, color: "#6b7280", fontSize: 12 }}>{a.knob ? `${a.knob.name} = ${a.knob.value}` : "—"}</td>
            <td style={td}><button style={btn()} onClick={() => go(a.tab as Tab)}>Open {a.tab} →</button></td>
          </tr>))}</tbody>
      </table>
    </div>)}
    <ThresholdsCard onChanged={onChanged} />
    <TestAlertCard />
  </>);
}

/** (B122; planning 10-27 15:10) superadmin only: send one test alert on every channel set, and show each channel's result.
 *  Its words are proposed until the owner approves them. */
function TestAlertCard() {
  const isOwner = useAuthStore((s) => s.user?.role === "superadmin");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<TestAlertAnswer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!isOwner) return null;
  const send = () => {
    setBusy(true); setErr(null); setAnswer(null);
    opsOverviewApi.testAlert().then(setAnswer).catch((e) => setErr((e as Error)?.message ?? String(e))).finally(() => setBusy(false));   // catch-reason: the refusal is shown (no channel set, or within the minute)
  };
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{TW.title}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{TW.about}</div>
        </div>
        <button style={actionBtn(true, busy)} disabled={busy} aria-label={TW.button} onClick={send}>{busy ? TW.sending : TW.button}</button>
      </div>
      {err && <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>{err}</div>}
      {answer && (
        <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 13 }}>
          {testAlertLines(answer).map((l, i) => <li key={i} style={l.ok === null ? { color: "#92400e" } : undefined}>{l.ok === null ? "" : l.ok ? "✓ " : "✗ "}{l.text}</li>)}
        </ul>)}
    </div>
  );
}

/** The alert rules' thresholds — each a registered setting, editable here. A change goes on the audit trail (a
 *  human.decision, with the reason) BEFORE it applies; if the trail can't be written, it does not apply. */
export function ThresholdsCard({ onChanged, only }: { onChanged: () => void; only?: string }) {
  const { track } = useProgress();
  const [rows, setRows] = useState<OpsThreshold[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  // (B113) what the last save of each setting answered: "applies to new runs · 3 running videos keep the old value"
  const [saved, setSaved] = useState<Record<string, string>>({});
  // (tf3-31, A17) a failed read is shown, never an empty list of settings
  const load = useCallback(() => { setReadErr(null); opsOverviewApi.settings().then((r) => { setRows(r.thresholds); setDraft({}); })
    .catch((e) => setReadErr(String((e as Error)?.message ?? e))); }, []);   // catch-reason: the error is shown in place of the settings
  useEffect(load, [load]);
  const save = async (t: OpsThreshold, value: number | string | null) => {
    const reason = window.prompt(value === null ? (t.engineOwned
      // (audit 10-25) a workers' setting resets to the ENGINE's own value: its .env layer when set, else the registered default
      ? `Reset “${t.label}”? The engine then uses its own value: its .env setting if it has one, else the registered default (${t.default}). Why?`
      : `Reset “${t.label}” to its default (${t.default})? Why?`) : `Change “${t.label}” from ${t.value ?? "a value not readable from here"} to ${value}? Why? (required — recorded on the audit trail)`);
    if (!reason?.trim()) return;
    try {
      const r = await track(value === null ? `Resetting ${t.label}` : `Changing ${t.label}`, opsOverviewApi.setThreshold(t.name, value, reason.trim()),
        { steps: ["Put the change on the audit trail", "Apply it", "Done"], detail: `${t.name}: ${t.value ?? "?"} → ${value ?? t.default}`,
          done: (x) => [`now ${x.value}`, savedLine(x)].filter(Boolean).join(" · ") });
      const line = savedLine(r);
      setSaved((s) => { const n = { ...s }; if (line) n[t.name] = line; else delete n[t.name]; return n; });
    } catch { /* shown in the window */ }
    load(); onChanged();
  };
  const list = (rows ?? []).filter((t) => !only || t.name === only);
  return (
    <div style={card}>
      {!only && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><b>Thresholds</b>
        <span style={{ fontSize: 12, color: "#6b7280" }}>each change is a human.decision on the audit trail, with its reason</span></div>}
      {readErr ? <div style={{ fontSize: 13, color: "#b91c1c" }}>The settings couldn't be read: {readErr}</div>
        : rows === null ? <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div> : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          {!only && <thead><tr><th style={th}>Setting</th><th style={th}>Value</th><th style={th}>Default</th><th style={th}>Last change</th><th style={th}></th></tr></thead>}
          <tbody>{list.map((t) => {
            // (2026-10-05) a setting with NO default (the face-match distance) shows empty until an operator sets it
            const d = draft[t.name] ?? (t.value === null || t.value === undefined ? "" : String(t.value));
            // session 6 W3: an enum setting is a choice (its value stays a string); a number is validated as before
            // (TF3-18) a text setting stays text: never read as a number
            const n: number | string = t.values || t.text ? (t.text ? d.trim() : d) : Number(d);
            // (P2d) a set setting: any subset of its offered values, as a comma list in their order ("" = none)
            const picked = t.multi ? d.split(",").filter(Boolean) : [];
            // eslint-disable-next-line no-control-regex
            const valid = t.text ? d.trim().length <= t.text.maxLength && !/[\u0000-\u001f\u007f]/.test(d)
              : t.multi ? picked.every((x) => t.values!.includes(x)) : t.values ? t.values.includes(d)
              : d !== "" && Number.isFinite(n as number) && (n as number) >= t.min && (n as number) <= t.max && (!t.integer || Number.isInteger(n))
                && !(t.exclusiveMin && (n as number) === t.min);
            return (
              <tr key={t.name}>
                <td style={td}><div>{t.label}{modeLabel(t.perRun) && <span style={{ fontSize: 11, color: t.perRun === "pinned" ? "#1d4ed8" : "#047857", marginLeft: 6 }}>{modeLabel(t.perRun)}</span>}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}><code>{t.name}</code> · {t.description}</div>
                  {saved[t.name] && <div style={{ fontSize: 11, color: "#92400e" }}>{saved[t.name]}</div>}</td>
                <td style={td}>{t.multi
                  ? <span style={{ display: "inline-flex", gap: 10, flexWrap: "wrap" }}>{t.values!.map((v) => (
                      <label key={v} style={{ fontSize: 12, display: "inline-flex", gap: 4, alignItems: "center" }}>
                        <input type="checkbox" checked={picked.includes(v)} onChange={(e) => setDraft({ ...draft,
                          [t.name]: t.values!.filter((x) => (x === v ? e.target.checked : picked.includes(x))).join(",") })} />{v}</label>))}
                      {!picked.length && <span style={{ fontSize: 11, color: "#6b7280" }}>{d === "" && t.value === null && !(t.name in draft) ? "—" : "none"}</span>}</span>
                  : t.values
                  ? <select aria-label={t.label} value={d} onChange={(e) => setDraft({ ...draft, [t.name]: e.target.value })}
                      style={{ padding: "3px 6px", border: "1px solid #d1d5db", borderRadius: 6 }}>
                      {/* (audit 10-25) no value known (the engine's not readable): an empty choice, never the first option shown as in force */}
                      {!t.values.includes(d) && <option value={d} disabled>—</option>}
                      {t.values.map((v) => <option key={v} value={v}>{v}</option>)}</select>
                  : <input aria-label={t.label} value={d} onChange={(e) => setDraft({ ...draft, [t.name]: e.target.value })}
                      {...(t.text ? { maxLength: t.text.maxLength } : {})}
                      style={{ width: t.text ? 180 : 80, padding: "3px 6px", border: `1px solid ${valid ? "#d1d5db" : "#dc2626"}`, borderRadius: 6 }} />} <span style={{ fontSize: 11, color: "#6b7280" }}>{t.unit}</span>
                  {(() => { const e = engineLine(t); return e && <div title={e.why} style={{ fontSize: 11, color: e.readable ? "#6b7280" : "#92400e" }}>{e.text}</div>; })()}</td>
                <td style={td}>{t.multi && t.default === "" ? "none" : t.default ?? <span style={{ color: "#92400e" }}>none — set it</span>}{t.source === "override" ? <span style={{ fontSize: 11, color: "#92400e" }}> · overridden</span> : null}</td>
                <td style={{ ...td, fontSize: 12, color: "#6b7280" }}>{t.updatedBy ? `${t.updatedBy} · ${new Date(t.updatedAt!).toLocaleString()} · “${t.reason}”` : "—"}</td>
                <td style={td}><span style={{ display: "inline-flex", gap: 4 }}>
                  <button style={actionBtn(true, !valid || n === t.value)} disabled={!valid || n === t.value} onClick={() => save(t, n)}>Save</button>
                  {t.source === "override" && <button style={btn()} onClick={() => save(t, null)}>Reset</button>}</span></td>
              </tr>);
          })}</tbody>
        </table>)}
    </div>
  );
}

const RUN_FILTERS: { id: string; label: string; key: string; sev: Severity }[] = [
  { id: "stalled", label: "Stalled", key: "stalled", sev: "amber" }, { id: "failing", label: "Failing (24 h)", key: "failing", sev: "red" },
  { id: "loose", label: "Loose ends", key: "loose", sev: "amber" }, { id: "overdue", label: "Overdue gates", key: "overdue", sev: "amber" },
  { id: "parked", label: "Parked at a gate", key: "parked", sev: "grey" }, { id: "active", label: "Active", key: "active", sev: "grey" },
];

function RunsTab({ o, onChanged }: { o: OpsOverview; onChanged: () => void }) {
  const { track } = useProgress();
  const [filter, setFilter] = useState("stalled");
  const [rows, setRows] = useState<OpsRunRow[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  // (tf3-31, A17) a failed read is shown, never "None."
  const load = useCallback(() => { setRows(null); setReadErr(null); setSel(new Set()); opsOverviewApi.runs(filter).then((r) => setRows(r.items))
    .catch((e) => setReadErr(String((e as Error)?.message ?? e))); }, [filter]);   // catch-reason: the error is shown in place of the runs
  useEffect(load, [load]);
  const c = o.runs?.counts ?? {};
  // Cancel = "we stopped it" · Fail = "it was wrong" (the evidence loop needs the difference). Both need a reason and
  // are recorded as a human.decision; both close the run's gates and unfinished steps and cascade.
  const bulk = async (kind: "cancel" | "fail" | "finish") => {
    const ids = [...sel];
    let reason = "finished from Operations";
    if (kind !== "finish") {
      const r = window.prompt(`${kind === "cancel" ? "Cancel" : "Fail"} ${ids.length} run(s). Why? (required — recorded with each ${kind === "cancel" ? "cancel" : "failure"})`);
      if (!r?.trim()) return;
      reason = r.trim();
    }
    const call = (id: string) => kind === "cancel" ? opsOverviewApi.cancelRun(id, reason) : kind === "fail" ? opsOverviewApi.failRun(id, reason) : opsOverviewApi.finishLooseEnds(id);
    const title = kind === "cancel" ? "Cancelling" : kind === "fail" ? "Failing" : "Finishing loose ends of";
    try {
      await track(`${title} ${ids.length} run(s)`, (async () => {
        let ok = 0; for (const id of ids) { const r = await call(id).catch(() => null); if (r?.ok) ok++; } return ok;  // catch-reason: a run whose action failed isn't counted: the window says "n of N done"
      })(), { steps: ["Through the orchestrator, one run at a time", "Done"],
        detail: kind === "finish" ? "open steps and gates closed; the backend gets each run's final state" : "gates and unfinished steps close; each run event updates the video's status and credits",
        done: (n) => `${n} of ${ids.length} done` });
    } catch { /* shown in the window */ }
    load(); onChanged();
  };
  return (
    <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {RUN_FILTERS.map((f) => {
          const n = c[f.key] ?? 0; const sev: Severity = n ? f.sev : "grey";
          return <button key={f.id} onClick={() => setFilter(f.id)} style={{ ...btn(filter === f.id), display: "inline-flex", gap: 6 }}>
            {f.label}<span style={{ padding: "0 6px", borderRadius: 8, background: SEV[sev].bg, color: SEV[sev].fg }}>{n}</span></button>;
        })}
      </div>
      {filter === "failing" && !!o.runs?.failingByReason.length && (
        <div style={card}><b style={{ fontSize: 13 }}>Failing, grouped by reason</b>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13 }}>{o.runs.failingByReason.map((g) => <li key={g.stepId + g.category}>{g.runs} run(s) · {g.stepId} · <code>{g.category}</code></li>)}</ul></div>)}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>{filter === "stalled" ? `no step progress for ${o.runs?.stallHours ?? 6}+ h and not parked at a gate (RUN_STALL_HOURS)` : ""}</span>
          <span style={{ display: "inline-flex", gap: 6 }}>
            {filter === "loose"
              ? <button style={actionBtn(true, !sel.size)} disabled={!sel.size} onClick={() => bulk("finish")}>Finish loose ends ({sel.size})</button>
              : <><button style={actionBtn(false, !sel.size)} disabled={!sel.size} onClick={() => bulk("fail")} title="it was wrong — counts as a failure">Fail selected ({sel.size})</button>
                  <button style={actionBtn(true, !sel.size)} disabled={!sel.size} onClick={() => bulk("cancel")} title="we stopped it">Cancel selected ({sel.size})</button></>}
          </span>
        </div>
        {readErr ? <div style={{ color: "#b91c1c", fontSize: 13 }}>The runs couldn't be read: {readErr}</div>
          : rows === null ? <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div> : !rows.length ? <div style={{ color: "#6b7280", fontSize: 13 }}>None.</div> : (
          <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}><input type="checkbox" aria-label="select all" checked={sel.size === rows.length} onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.runId)) : new Set())} /></th>
              <th style={th}>Run</th><th style={th}>Workflow</th><th style={th}>Status</th><th style={th}>Step / reason</th><th style={th}>Last activity</th><th style={th}>Attempts</th><th style={th}>Spent</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.runId}>
                <td style={td}><input type="checkbox" checked={sel.has(r.runId)} onChange={(e) => { const n = new Set(sel); e.target.checked ? n.add(r.runId) : n.delete(r.runId); setSel(n); }} /></td>
                <td style={td}><Link to={`/admin/runs?run=${r.runId}`} style={{ fontFamily: "monospace", fontSize: 12 }}>{r.runId.slice(0, 8)}…</Link></td>
                <td style={td}>{r.workflowId}</td><td style={td}>{r.status}</td>
                <td style={td}>{r.stepId ?? "—"}{r.failureCategory ? <span style={{ color: "#b91c1c" }}> · {r.failureCategory}</span> : null}</td>
                <td style={td}>{new Date(r.lastActivity).toLocaleString()}</td><td style={td}>{r.attempts}</td><td style={td}>{usd(r.spentUsdCents)}</td>
              </tr>))}</tbody>
          </table></div>)}
      </div>
    </>
  );
}

function SchedulesTab({ o, onChanged }: { o: OpsOverview; onChanged: () => void }) {
  const { track } = useProgress();
  const act = async (id: string, name: string, action: "pause" | "resume") => {
    try { await track(`${action === "pause" ? "Pausing" : "Resuming"} “${name}”`, opsOverviewApi.seriesAction(id, action, `${action}d from Operations`)); } catch { /* shown */ }
    onChanged();
  };
  const cap = o.thresholds.seriesPerHour;
  return (
    <>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between" }}><b>Autopilot series</b>
          <span style={{ fontSize: 12, color: "#6b7280" }}>limit {cap} video(s) / series / hour · <code>AUTOPILOT_MAX_PER_SERIES_PER_HOUR</code> · counted and alerted; enforced in the scheduler by O7</span></div>
        <ThresholdsCard onChanged={onChanged} only="AUTOPILOT_MAX_PER_SERIES_PER_HOUR" />
        {!o.series.length ? <p style={{ color: "#6b7280", fontSize: 13 }}>No series.</p> : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
            <thead><tr><th style={th}>Series</th><th style={th}>Status</th><th style={th}>Last hour</th><th style={th}>Last 24 h</th><th style={th}>Next slot</th><th style={th}></th></tr></thead>
            <tbody>{o.series.map((s) => (
              <tr key={s.id} style={s.overLimit ? { background: "#fef2f2" } : undefined}>
                <td style={td}>{s.name}{s.personaKey ? <span style={{ color: "#6b7280", fontSize: 11 }}> · persona</span> : null}</td>
                <td style={td}>{s.status}</td>
                <td style={td}><RateBar n={s.lastHour} cap={cap} /></td>
                <td style={td}>{s.lastDay}</td>
                <td style={td}>{s.nextScheduledAt ? new Date(s.nextScheduledAt).toLocaleString() : "—"}</td>
                <td style={td}>{s.status === "active" ? <button style={btn(s.overLimit)} onClick={() => act(s.id, s.name, "pause")}>Pause</button>
                  : <button style={btn()} onClick={() => act(s.id, s.name, "resume")}>Resume</button>}</td>
              </tr>))}</tbody>
          </table>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        <div style={card}><b>Prospect runner</b><p style={{ fontSize: 13, margin: "6px 0 0" }}>{o.prospect.enabled ? "on" : "off"} · last night {o.prospect.lastNight ?? "never"} · queued {o.prospect.queued} · failed {o.prospect.failed}</p></div>
        <div style={card}><b>Growth mood pictures</b><p style={{ fontSize: 13, margin: "6px 0 0" }}>{o.firstFrames.lastHour} in the last hour · {o.firstFrames.failedLastDay} failed in 24 h</p></div>
      </div>
    </>
  );
}

/** Phase 4: the checks the operator used to run by hand, run by the system. Each service checks its OWN connections
 *  from inside its own container (the backend, the workers, ingest). ✗ = fix it (the fix is shown); ⚠ = worth a look. */
function HealthTab({ o, onChanged }: { o: OpsOverview; onChanged: () => void }) {
  const { track } = useProgress();
  const [h, setH] = useState<{ checks: HealthCheck[]; ranAt: string } | null>(o.health);
  const recheck = async () => {
    try { const r = await track("Re-checking everything", opsOverviewApi.recheck(), { steps: ["Each service checks its own connections, from inside", "Done"], detail: "the backend, the workers, ingest", done: (x) => `${x.checks.filter((c) => c.status === "fail").length} ✗ · ${x.checks.filter((c) => c.status === "warn").length} ⚠` }); setH(r); } catch { /* shown */ }
    onChanged();
  };
  if (!h) return <div style={card}>Health results unavailable. <button style={btn()} onClick={recheck}>Check now</button></div>;
  const sections = [...new Set(h.checks.map((c) => c.section))];
  const icon = { ok: "✓", warn: "⚠", fail: "✗" } as const, col = { ok: "#16a34a", warn: "#b45309", fail: "#dc2626" } as const;
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>checked {new Date(h.ranAt).toLocaleString()} · every 5 min · {h.checks.filter((c) => c.status === "fail").length} ✗ · {h.checks.filter((c) => c.status === "warn").length} ⚠ · {h.checks.filter((c) => c.status === "ok").length} ✓</span>
        <button style={btn(true)} onClick={recheck}>Re-check now</button>
      </div>
      {sections.map((s) => (
        <div key={s} style={card}><b style={{ fontSize: 13 }}>{s}</b>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}><tbody>
            {h.checks.filter((c) => c.section === s).sort((a, b) => ({ fail: 0, warn: 1, ok: 2 })[a.status] - ({ fail: 0, warn: 1, ok: 2 })[b.status]).map((c) => (
              <tr key={c.name}>
                <td style={{ ...td, width: 22, color: col[c.status], fontWeight: 700 }}>{icon[c.status]}</td>
                <td style={{ ...td, width: 230 }}>{c.name}</td>
                <td style={td}>{c.detail}{c.fix ? <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 2 }}>fix: {c.fix}</div> : null}</td>
              </tr>))}
          </tbody></table>
        </div>))}
    </>
  );
}

/** The rate against its limit, as a picture: a short bar that turns red past the cap. */
function RateBar({ n, cap }: { n: number; cap: number }) {
  const pct = Math.min(100, (n / Math.max(cap, 1)) * 100);
  const over = n > cap;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 70, height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}><span style={{ display: "block", height: "100%", width: `${pct}%`, background: over ? "#dc2626" : "#16a34a" }} /></span>
    <span style={{ fontSize: 12, color: over ? "#b91c1c" : "#374151", fontWeight: over ? 700 : 400 }}>{n} of max {cap}</span></span>;
}

function ProvidersTab({ o, onChanged }: { o: OpsOverview; onChanged: () => void }) {
  const { track } = useProgress();
  // the provider pause contract: until the engine's breaker lands (session 5, O7) the route answers 501 and the
  // controls show disabled with that reason — the page is ready the day the breaker is.
  const [ps, setPs] = useState<{ available: boolean; byModule: Record<string, ProviderState>; note: string } | null>(null);
  const loadPs = useCallback(() => {
    opsOverviewApi.providerState()
      .then((r) => setPs({ available: true, byModule: Object.fromEntries(r.providers.map((p) => [p.moduleId, p])), note: "" }))
      .catch((e) => setPs({ available: false, byModule: {}, note: /501|NOT_IMPLEMENTED/.test(String(e)) ? "Pause / resume arrives with the engine's provider circuit breaker (session 5, O7). The contract is fixed; these controls turn on when it lands." : `provider state unavailable: ${String(e)}` }));  // catch-reason: the failure IS the answer: shown as "provider state unavailable" with the error
  }, []);
  useEffect(loadPs, [loadPs]);
  const act = async (moduleId: string, action: "pause" | "resume") => {
    const reason = window.prompt(`${action === "pause" ? "Pause" : "Resume"} ${moduleId}? Why? (required — recorded as a human.decision)${action === "pause" ? "\nNew orders are still accepted; steps that need it wait (queue, don't block)." : ""}`);
    if (!reason?.trim()) return;
    try { await track(`${action === "pause" ? "Pausing" : "Resuming"} ${moduleId}`, opsOverviewApi.providerAction(moduleId, action, reason.trim())); } catch { /* shown */ }
    loadPs(); onChanged();
  };
  if (!o.providers.length) return <div style={{ ...card, color: "#6b7280" }}>No model calls in the last hour.</div>;
  const color = (s: string) => (s === "refusing" ? SEV.red : s === "degraded" ? SEV.amber : { bg: "#dcfce7", fg: "#166534" });
  return (
    <div style={card}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        {ps && !ps.available && <caption style={{ captionSide: "top", textAlign: "left", fontSize: 12, color: "#6b7280", paddingBottom: 8 }}>{ps.note}</caption>}
        <thead><tr><th style={th}>Provider</th><th style={th}>State</th><th style={th}>Calls (1 h)</th><th style={th}>Errors</th><th style={th}>No-run calls</th><th style={th}>Spend (1 h)</th><th style={th}>Pause</th><th style={th}></th></tr></thead>
        <tbody>{o.providers.map((p) => (
          <tr key={p.provider}>
            <td style={td}>{p.provider}</td>
            <td style={td}><span style={{ padding: "1px 8px", borderRadius: 8, fontSize: 12, background: color(p.state).bg, color: color(p.state).fg }}>{p.state}</span></td>
            <td style={td}>{p.calls}</td>
            <td style={td}>{p.errors ? Object.entries(p.byError).map(([k, v]) => `${v} ${k}`).join(" · ") : "—"}</td>
            <td style={td} title="calls with no run — background work (a scheduler)">{p.background}</td>
            <td style={td}>{usd(p.costUsdCents)}</td>
            <td style={td}>{(() => {
              const st = ps?.byModule[p.provider]; const off = !ps?.available;
              if (st?.state === "paused") return <span style={{ fontSize: 12 }}>paused {st.since ? new Date(st.since).toLocaleString() : ""} by {st.by} · “{st.reason}” · {st.waitingSteps} waiting{st.pastWaitLimit ? ` (${st.pastWaitLimit} past the limit)` : ""}{" "}
                <button style={actionBtn(false, off)} disabled={off} onClick={() => act(p.provider, "resume")}>Resume</button></span>;
              return <button style={actionBtn(p.state === "refusing", off)} disabled={off} title={off ? ps?.note : "steps that need it wait; new orders are still accepted"} onClick={() => act(p.provider, "pause")}>Pause</button>;
            })()}</td>
            <td style={td}><Link to="/admin/providers" style={{ fontSize: 12 }}>Test / edit key →</Link></td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function SpendTab({ o }: { o: OpsOverview }) {
  const hours = o.spend.hours.slice(-48);
  const max = useMemo(() => Math.max(1, ...hours.map((h) => h.total)), [hours]);
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between" }}><b>Model and image spend, per hour (last 48 h)</b>
        <span style={{ fontSize: 12, color: "#6b7280" }}>this hour {usd(o.spend.thisHourUsdCents)} · average {usd(o.spend.avgHourUsdCents)} · spike at {o.thresholds.spendSpikeFactor}× (<code>OPS_SPEND_SPIKE_FACTOR</code>)</span></div>
      <svg width="100%" height="150" viewBox={`0 0 ${Math.max(hours.length, 1) * 12} 150`} preserveAspectRatio="none" role="img" aria-label="spend per hour" style={{ marginTop: 10 }} data-visual>
        {hours.map((h, i) => { const hr = (h.inRun / max) * 140, hb = (h.background / max) * 140; return (
          <g key={h.hour}><rect x={i * 12 + 1} y={150 - hr} width={10} height={hr} fill="#2563eb"><title>{h.hour}: in runs {usd(h.inRun)}</title></rect>
            <rect x={i * 12 + 1} y={150 - hr - hb} width={10} height={hb} fill="#f97316"><title>{h.hour}: background (no run) {usd(h.background)} · {h.backgroundCalls} calls</title></rect></g>); })}
      </svg>
      <div style={{ fontSize: 12, marginTop: 4 }}><span style={{ color: "#2563eb" }}>■</span> inside a run &nbsp; <span style={{ color: "#f97316" }}>■</span> background — no run (a scheduler; how AP-1 would have shown)</div>
      {/* session 5: who spent it, over the window (the `source` on every model call) */}
      {o.spend.bySource && Object.keys(o.spend.bySource).length > 0 && (
        <table style={{ marginTop: 12, fontSize: 13, borderCollapse: "collapse" }} data-visual>
          <thead><tr><th style={{ textAlign: "left", padding: "2px 12px 2px 0" }}>By source</th><th style={{ textAlign: "right", padding: "2px 12px" }}>Calls</th><th style={{ textAlign: "right", padding: "2px 0" }}>Spend</th></tr></thead>
          <tbody>{Object.entries(o.spend.bySource).sort((a, b) => b[1].costUsdCents - a[1].costUsdCents).map(([src, v]) => (
            <tr key={src}><td style={{ padding: "2px 12px 2px 0" }}>{src.replace("_", " ")}</td><td style={{ textAlign: "right", padding: "2px 12px" }}>{v.calls}</td><td style={{ textAlign: "right" }}>{usd(v.costUsdCents)}</td></tr>))}
          </tbody>
        </table>)}
    </div>
  );
}

/** Session 7 §7d: orders accepted NOT READY while intake only warns — per agency and video type, and how many of those
 *  then stopped on the gap. The evidence for enforcing readiness agency by agency (tenants.intake_readiness_mode). */
function ReadinessWarnings() {
  const [rows, setRows] = useState<ReadinessWarningRow[] | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  // (tf3-31, A17) a failed read is shown, never "no warnings"
  useEffect(() => { opsOverviewApi.readinessWarnings(30).then((r) => setRows(r.rows))
    .catch((e) => setReadErr(String((e as Error)?.message ?? e))); }, []);   // catch-reason: the error is shown in place of the warnings
  if (readErr) return <div style={{ ...card, color: "#b91c1c", fontSize: 13 }}>The orders accepted not ready couldn't be read: {readErr}</div>;
  if (!rows || rows.length === 0) return null;
  return (
    <div style={card}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Orders accepted not ready (last 30 days) — readiness is only warned for these agencies</div>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr><th style={th}>Agency</th><th style={th}>Video type</th><th style={th}>Warned</th><th style={th}>Then stopped on the gap</th><th style={th}>Last</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.agency}|${r.videoTypeId}`}><td style={td}>{r.agency}</td><td style={td}>{r.videoTypeId ?? "—"}</td><td style={td}>{r.warned}</td>
            <td style={td}>{r.settledNotReady}</td><td style={td}>{r.lastAt ? new Date(r.lastAt).toLocaleDateString() : "—"}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}
